import { useMemo } from 'react'
import { Link, NavLink, Outlet } from 'react-router'
import type { JSX } from 'react'
import { useAuth } from '@/auth/context'
import { SessionExpired } from '@/auth/SessionExpired'
import { safeStorage } from '@/lib/storage'
import { AccountStatus } from '@/pages/AccountStatus'
import { Lockup } from '@/shell/Lockup'
import { SupportAccessBanner } from '@/shell/SupportAccessBanner'
import { SupportReadOnlyNotice } from '@/shell/SupportReadOnlyNotice'
import { AccountMenu, Breadcrumbs } from '@/shell/TopBar'
import { moduleById, rememberedModule } from '@/shell/navigation'
import { useHashScroll } from '@/shell/useHashScroll'
import { may, useActiveTenant } from '@/tenant/context'
import { SETTINGS_GROUPS, SETTINGS_PANELS } from './panels'

/**
 * The frame every settings screen sits in — a page of its own, outside
 * `AppShell` (owner decision 2026-09-16: *"settings should be a separate page,
 * not an underlying page"*).
 *
 * `shell.html#settings` draws settings inside the module rail, and that is how
 * it shipped from S-D3 until now. The shape was wrong for what the screen had
 * become: the consolidation of 2026-09-15 took it to twenty panels under three
 * headings, which is a second navigation the width of the first, sat beside a
 * rail whose sections have nothing to do with any of them. So the rail and the
 * module switcher go, the settings groups become the only left nav, and one
 * link leads back — to the module the tenant was in, read from the same
 * remembered choice `AppShell` writes, because *Back* should return you to
 * where you were and not to a default home you may never use.
 *
 * What stays is everything that was never navigation: the support banner, the
 * account-status and read-only notices, the session-expired dialog and the
 * `#fragment` scroll. Each of those is a property of *being signed in*, not of
 * the shell, and a frame that dropped one would make settings the one screen
 * where a borrowed session or an unconfirmed address went unmentioned.
 *
 * The bar keeps the two-crumb trail SHELL-19 settled on — the organisation and
 * *Settings* — and the account menu at every width: there is no drawer here to
 * carry its rows below 1100px, so the avatar is the way to sign out and to
 * billing on a phone as well as a desktop. The bar is sticky and the section
 * list is its own scroll column under it (`app.css`), since the list is twenty
 * rows and the panels are long forms — both have to reach their ends.
 */
export function SettingsShell(): JSX.Element {
  const { tenant } = useActiveTenant()
  const { expired } = useAuth()
  const storage = useMemo(() => safeStorage(), [])
  const back = moduleById(rememberedModule(storage))
  useHashScroll()

  return (
    <div className="app-settings-page">
      <SupportAccessBanner />
      <header className="app-settings-page__bar">
        {/* Leftmost, and it says *dashboard* rather than the module's name
            (owner, 2026-09-16, on first look): both homes are dashboards, and
            the word people reach for is the destination, not the half of the
            product it is in. The target is still the remembered module's home. */}
        <Link className="ar-btn ar-btn--ghost ar-btn--sm" to={back.home}>
          <span aria-hidden="true">←</span> Back to dashboard
        </Link>
        <Breadcrumbs
          crumbs={[{ label: tenant.name, to: '/organisations' }, { label: 'Settings' }]}
        />
        <span className="app-spacer" />
        <Link className="app-public__lockup" to={back.home} aria-label="Home">
          <Lockup size="1.5rem" />
        </Link>
        <AccountMenu />
      </header>
      <main className="app-settings-page__body">
        <AccountStatus />
        <SupportReadOnlyNotice />
        <Outlet />
      </main>
      {expired ? <SessionExpired /> : null}
    </div>
  )
}

/**
 * The panels and their sub-nav — grouped under three headings since the
 * settings consolidation of 2026-09-15 brought the outreach and SEO settings in
 * from the working pages. A pathless layout under `SettingsShell`, so a screen
 * that belongs to settings but not to the list (the invite card, parity
 * P-INV-01) can sit in the same frame without the sub-nav beside it.
 *
 * A panel is hidden — not disabled — when the role cannot read it, because
 * "you may not see this" and "this does not exist yet" are different statements
 * and showing both the same way would teach people to ignore both. The API
 * refuses regardless; this only keeps the UI from offering a 403.
 */
export function SettingsLayout(): JSX.Element {
  const { membership } = useActiveTenant()

  return (
    <>
      <h1 className="ar-h1">Settings</h1>
      <div className="app-settings">
        <nav className="app-settings__nav" aria-label="Settings sections">
          {SETTINGS_GROUPS.map((group) => {
            const panels = SETTINGS_PANELS.filter(
              (panel) =>
                panel.group === group && (!panel.permission || may(membership, panel.permission)),
            )
            if (panels.length === 0) return null
            return (
              <div key={group} className="app-settings__group">
                <h2>{group}</h2>
                {panels.map((panel) =>
                  panel.to ? (
                    <NavLink key={panel.label} to={panel.to}>
                      {panel.label}
                    </NavLink>
                  ) : (
                    <span key={panel.label} aria-disabled="true">
                      {panel.label}
                      <span className="app-nav__soon">Soon</span>
                    </span>
                  ),
                )}
              </div>
            )
          })}
        </nav>
        <div>
          <Outlet />
        </div>
      </div>
    </>
  )
}
